import './agenda-appointment-slot-list-item.scss';

import { useMutation } from '@apollo/client';
import { first, kebabCase, last } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { AgendaAppointmentModalAction } from '../../../data/data/enums';
import { AppointmentModalData } from '../../../data/data/types';
import {
  AgendaAppointmentItemDataFragment,
  AgendaBlockItemDataFragment,
  AgendaSlotItemDataFragment,
  ConsultType,
  GetAgendaItemsDocument,
  SlotType,
  UpdateAgendaAppointmentItemStatusDocument,
} from '../../../data/types/api/graphql';
import { formatDateTimeToTime } from '../../../data/utils/date.utils';
import { useFormatters } from '../../../data/utils/formatters.utils';
import { makeKeyDownHandler } from '../../../data/utils/make-key-down-handler';
import { useNavigateWithHealthProvider } from '../../../data/utils/router.utils';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../elements/button/button';
import { CustomFormattedMessage } from '../../../elements/intl/custom-formatted-message';
import { Tag, TagType } from '../../../elements/tag/tag';
import {
  getAgendaAppointmentAppointmentInput,
  getAgendaSlotAppointmentInput,
} from '../modals/agenda-appointment-modal/agenda-appointment-modal';
import { AgendaAppointmentToggleList } from './agenda-appointment-toggle-list';

enum AgendaAppointmentSlotListActionType {
  NONE = 'NONE',
  APPOINTMENT = 'APPOINTMENT',
  ADD_APPOINTMENT = 'ADD_APPOINTMENT',
  TOGGLE = 'TOGGLE',
}

export enum AgendaAppointmentSlotListItemType {
  DEFAULT = 'DEFAULT',
}

type AgendaAppointmentSlotListItemProps = {
  healthProviderId: string | null | undefined;
  healthProfessionalId: string | null | undefined;
  type?: AgendaAppointmentSlotListItemType;
  compact?: boolean;
  agendaBlockItem: AgendaBlockItemDataFragment | null | undefined;
  agendaSlotItem: AgendaSlotItemDataFragment;
  showAppointmentModal?: (
    slotType: SlotType,
    value: AppointmentModalData | null
  ) => void;
};

export function AgendaAppointmentSlotListItem({
  compact,
  healthProviderId,
  healthProfessionalId,
  agendaBlockItem,
  agendaSlotItem,
  showAppointmentModal,
  ...props
}: AgendaAppointmentSlotListItemProps) {
  const intl = useIntl();

  const [toggleState, setToggleState] = useState<boolean>(false);

  const { getFullName, getBirthDate } = useFormatters();

  // is in past less then today
  const isInPast = useMemo(() => {
    const appointmentDate = new Date(agendaSlotItem.startTime);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return appointmentDate < today;
  }, [agendaSlotItem]);

  const { agendaAppointmentItem, agendaAppointmentItems } = useMemo(() => {
    const appointments = agendaSlotItem.appointments ?? [];
    return {
      agendaAppointmentItem: first(appointments),
      agendaAppointmentItems: appointments,
    };
  }, [agendaSlotItem]);

  const type = useMemo(() => {
    if (props.type) {
      return props.type;
    }

    if (agendaSlotItem.slotType === SlotType.Phone) {
      return 'telephone';
    }
    if (agendaSlotItem.slotType === SlotType.Bulk) {
      return 'bulk';
    }

    if (agendaAppointmentItem) {
      return kebabCase(agendaAppointmentItem.consultType);
    }

    return AgendaAppointmentSlotListItemType.DEFAULT;
  }, [props.type, agendaSlotItem.slotType, agendaAppointmentItem]);

  const getIcon = useCallback((location: ConsultType) => {
    if (location === ConsultType.Else) return 'clock';
    if (location === ConsultType.Bulk) return 'user-group';
    return kebabCase(location);
  }, []);

  const {
    status,
    completed,
    title,
    location,
    time,
    action,
    hasItems,
    tags,
    irregular,
  } = useMemo(() => {
    const startTimeString = agendaSlotItem.startTime;

    let endTimeString = agendaSlotItem.endTime;

    const lastAppointment =
      agendaAppointmentItems.length > 0 ? last(agendaAppointmentItems) : null;
    if (lastAppointment) {
      endTimeString = lastAppointment.endTime;
    }

    const startTime = formatDateTimeToTime(startTimeString, intl);
    const endTime = formatDateTimeToTime(endTimeString, intl);

    const irregularSlot = agendaSlotItem.irregular;

    // phonelist
    if (agendaSlotItem.slotType === SlotType.Phone) {
      return {
        hasItems:
          agendaAppointmentItems.filter(option => !option.isCompleted).length >
          0,
        status: false,
        completed:
          agendaAppointmentItems.filter(option => !option.isCompleted)
            .length === 0,
        title:
          agendaAppointmentItems.length === 1 ? (
            <CustomFormattedMessage id="common.agenda-appointment.appointment" />
          ) : (
            <CustomFormattedMessage
              id="common.agenda-appointment.appointments"
              values={{ count: agendaAppointmentItems.length }}
            />
          ),

        location: ConsultType.Telephone,
        time: `${startTime} - ${endTime}`,
        action: AgendaAppointmentSlotListActionType.TOGGLE,
        tags: null,
        irregular: false,
      };
    }

    // visit list
    if (agendaSlotItem.slotType === SlotType.Visit) {
      return {
        hasItems: false,
        status: false,
        completed:
          agendaAppointmentItems.filter(option => !option.isCompleted)
            .length === 0,
        title:
          agendaAppointmentItems.length === 1 ? (
            <CustomFormattedMessage id="common.agenda-appointment.appointment" />
          ) : (
            <CustomFormattedMessage
              id="common.agenda-appointment.appointments"
              values={{ count: agendaAppointmentItems.length }}
            />
          ),
        location: agendaAppointmentItem?.consultType,
        time: `${startTime} - ${endTime}`,
        action: AgendaAppointmentSlotListActionType.TOGGLE,
        tags: null,
        irregular: false,
      };
    }

    // bulk list
    if (agendaSlotItem.slotType === SlotType.Bulk) {
      return {
        hasItems: false,
        status: false,
        completed: false,
        title: `${agendaSlotItem.bulkSlotInfo?.title} (${agendaAppointmentItems.length}/${agendaSlotItem.slots})`,
        location: ConsultType.Bulk,
        time: `${startTime} - ${endTime}`,
        action: AgendaAppointmentSlotListActionType.TOGGLE,
        tags: null,
        irregular: false,
      };
    }

    // else / exception
    if (agendaAppointmentItem) {
      if (
        [ConsultType.Else, ConsultType.Exception].includes(
          agendaAppointmentItem.consultType
        )
      ) {
        return {
          hasItems: false,
          status: true,
          completed: agendaAppointmentItem.isCompleted,
          title: agendaAppointmentItem.title,
          location: ConsultType.Else,
          time: `${startTime} - ${endTime}`,
          action: AgendaAppointmentSlotListActionType.NONE,
          tags: null,
          irregular: irregularSlot,
        };
      }

      return {
        hasItems: false,
        status: true,
        completed: agendaAppointmentItem.isCompleted,
        title: (
          <>
            {getFullName(agendaAppointmentItem.patient)}
            <time>{getBirthDate(agendaAppointmentItem.patient)}</time>
          </>
        ),
        location: agendaAppointmentItem.consultType,
        time: `${startTime} - ${endTime}`,
        action: AgendaAppointmentSlotListActionType.APPOINTMENT,
        tags: (
          <>
            {!agendaAppointmentItem.patient?.dutchInsurance && (
              <Tag type={TagType.NOINSURANCE} />
            )}
            {agendaAppointmentItem.patient?.noShowProtocol && (
              <Tag type={TagType.NOSHOWPROTOCOL} />
            )}
          </>
        ),
        irregular: irregularSlot,
      };
    }

    return {
      status: false,
      completed: false,
      title: null,
      location: null,
      time: `${startTime} - ${endTime}`,
      action: AgendaAppointmentSlotListActionType.ADD_APPOINTMENT,
      tags: null,
      irregular: irregularSlot,
    };
  }, [
    agendaSlotItem,
    intl,
    agendaAppointmentItems,
    agendaAppointmentItem,
    getFullName,
    getBirthDate,
  ]);

  const onSelect = useCallback(
    (slotType: SlotType, item?: AgendaAppointmentItemDataFragment) => {
      const values = item
        ? {
            agendaAppointmentItem: getAgendaAppointmentAppointmentInput(item),
          }
        : {
            action: AgendaAppointmentModalAction.CREATE,
            agendaAppointmentItem: getAgendaSlotAppointmentInput(
              agendaSlotItem,
              healthProfessionalId
            ),
          };

      showAppointmentModal?.(slotType, { healthProviderId, ...values });
    },
    [
      agendaSlotItem,
      healthProfessionalId,
      healthProviderId,
      showAppointmentModal,
    ]
  );

  const onHandleToggleAppointments = useCallback(() => {
    setToggleState(prevState => !prevState);
  }, []);

  const attributes = useMemo(
    () => ({
      role: 'button',
      tabIndex: 0,
      onClick: () => {
        if (
          agendaSlotItem.slotType === SlotType.Phone ||
          agendaSlotItem.slotType === SlotType.Visit ||
          agendaSlotItem.slotType === SlotType.Bulk
        ) {
          onHandleToggleAppointments();
        } else {
          onSelect(agendaSlotItem.slotType, agendaAppointmentItem);
        }
      },
      onKeyDown: makeKeyDownHandler(() =>
        onSelect(agendaSlotItem.slotType, agendaAppointmentItem)
      ),
    }),
    [
      agendaAppointmentItem,
      agendaSlotItem.slotType,
      onSelect,
      onHandleToggleAppointments,
    ]
  );

  const [
    updateAgendaAppointmentItemStatus,
    { loading: updateAgendaAppointmentItemStatusLoading },
  ] = useMutation(UpdateAgendaAppointmentItemStatusDocument, {
    refetchQueries: [GetAgendaItemsDocument],
  });

  const onHandleAppointmentStatus = useCallback(() => {
    if (agendaAppointmentItem) {
      updateAgendaAppointmentItemStatus({
        variables: {
          id: agendaAppointmentItem.id,
          status: !agendaAppointmentItem?.isCompleted,
        },
      });
    }
  }, [agendaAppointmentItem, updateAgendaAppointmentItemStatus]);

  const navigate = useNavigateWithHealthProvider();

  const onHandleAppointmentChat = useCallback(() => {
    if (agendaAppointmentItem) {
      navigate(`/conversations/${agendaAppointmentItem.conversation?.id}`);
    }
  }, [agendaAppointmentItem, navigate]);

  const classNames = useMemo(() => {
    // Start with the base class and type modifier
    const baseClass = `appointment-slot-list-item appointment-slot-list-item--${kebabCase(
      type
    )}`;

    // Add conditional classes only when needed
    const conditionalClasses = [
      completed && 'completed',
      compact && 'compact',
      !hasItems && 'deselected',
      irregular && 'irregular',
    ]
      .filter(Boolean)
      .join(' ');

    // Combine and return
    return conditionalClasses
      ? `${baseClass} ${conditionalClasses}`
      : baseClass;
  }, [compact, completed, hasItems, irregular, type]);

  return (
    <>
      <div {...attributes} className={classNames}>
        {agendaSlotItem.slotType !== SlotType.Phone &&
          !agendaBlockItem?.corrupt && (
            <div className="appointment-slot-list-item__status">
              {status && (
                <Button
                  type={ButtonType.TRANSPARENT}
                  icon={ButtonIcon.CHECK}
                  disabled={updateAgendaAppointmentItemStatusLoading}
                  onClick={onHandleAppointmentStatus}
                />
              )}
            </div>
          )}
        <div className="appointment-slot-list-item__time">
          {time && <p>{time}</p>}
        </div>
        <div className="appointment-slot-list-item__location">
          {location && (
            <p>
              <i className={`icon ${getIcon(location)}`} />
              <CustomFormattedMessage
                id={`common.agenda-appointment-type.${kebabCase(location)}`}
              />
            </p>
          )}
        </div>

        <div className="appointment-slot-list-item__content">
          {title && <p>{title}</p>}
        </div>
        <div className="appointment-slot-list-item__tags">{tags}</div>

        {action !== AgendaAppointmentSlotListActionType.NONE && (
          <div className="appointment-slot-list-item__action">
            {agendaAppointmentItem?.conversation != null &&
              action === AgendaAppointmentSlotListActionType.APPOINTMENT && (
                <Button
                  type={ButtonType.TRANSPARENT}
                  icon={ButtonIcon.COMMENTS}
                  onClick={onHandleAppointmentChat}
                />
              )}

            {action === AgendaAppointmentSlotListActionType.TOGGLE && (
              <Button
                type={ButtonType.TRANSPARENT}
                icon={ButtonIcon.CHEVRON}
                onClick={onHandleToggleAppointments}
              />
            )}

            {action === AgendaAppointmentSlotListActionType.ADD_APPOINTMENT && (
              <Button
                type={ButtonType.TRANSPARENT}
                icon={ButtonIcon.PLUS}
                disabled={isInPast}
                onClick={() => onSelect(agendaSlotItem.slotType)}
              />
            )}
          </div>
        )}
      </div>

      {toggleState && (
        <AgendaAppointmentToggleList
          compact={compact}
          healthProviderId={healthProviderId}
          healthProfessionalId={healthProfessionalId}
          agendaSlotItem={agendaSlotItem}
          agendaAppointmentItems={agendaAppointmentItems}
          showAppointmentModal={showAppointmentModal}
        />
      )}
    </>
  );
}
